import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import React from 'react';

import { useAccount, useGame } from '~/apis';
import { useTranslation } from '~/app';
import { isGameplayHistoryReplayEnabled } from '~/app/config';
import { getRouteHead } from '~/app/routeHead';
import { ROUTE_404, ROUTE_GAMES } from '~/app/routes';
import ReplayGame from '~/replay/ReplayGame';
import { UserRole } from '~/server/src/dtos';

export const Route = createFileRoute('/games/$id/replay')({
  component: GameReplayRoute,
  head: ({ params }) => getRouteHead({ title: params.id ? `Replay · ${params.id}` : 'Replay' }),
});

function GameReplayRoute() {
  const { id } = Route.useParams();
  return <GameReplayRouteInner gameId={id} />;
}

function GameReplayRouteInner({ gameId }: { gameId: string }) {
  const { data: account } = useAccount();
  const {
    data: gameState,
    isLoading,
    error,
  } = useGame(gameId, {
    enabled: !!gameId && isGameplayHistoryReplayEnabled,
  });
  const canUseFullView = account?.role === UserRole.Admin;

  if (!isGameplayHistoryReplayEnabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (!gameId || error || (!isLoading && !gameState)) {
    return <ReplayByGameError />;
  }

  if (isLoading || !gameState) {
    return <ReplayByGameLoading />;
  }

  return <ReplayGame gameState={gameState} closeTo={ROUTE_GAMES} canUseFullView={canUseFullView} />;
}

function ReplayByGameLoading() {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-100">
      <p className="text-gray-600">{t('replay_loading')}</p>
    </div>
  );
}

function ReplayByGameError() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-gray-900">
          {t('games_replay_notAvailable')}
        </h1>
        <p className="mb-6 text-sm text-gray-600">{t('games_history_error')}</p>
        <button
          type="button"
          onClick={() => navigate({ to: ROUTE_GAMES, replace: true })}
          className="rounded-md bg-[#405820] px-4 py-2 font-medium text-white hover:bg-[#405820]/90"
        >
          {t('button_back')}
        </button>
      </div>
    </div>
  );
}
